import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { TraceSpan } from "../../redux/reducers/sessionTraceSlice";
import { CompletionSpanDetailView, normalizeLLMCompletion, hasLLMContent, isSupportedLLMProvider } from "./CompletionSpanDetailView";
import { ToolSpanDetailView } from "./ToolSpanDetailView";
import { LogEventSpanDetailView, parseLogEventData } from "./LogEventSpanDetailView";
import { TextToSpeechSpanDetailView, parseTextToSpeechEventData } from "./TextToSpeechSpanDetailView";
import { StopEventSpanDetailView, parseStopEventData } from "./StopEventSpanDetailView";
import { AudioEventSpanDetailView, parseAudioEventData } from "./AudioEventSpanDetailView";
import { StartSessionEventSpanDetailView, parseStartSessionEventData } from "./StartSessionEventSpanDetailView";
import { InputEventSpanDetailView, parseInputEventData, isInputEventSpan } from "./InputEventSpanDetailView";
import { LoggerSpanDetailView, parseLoggerSpanData } from "./LoggerSpanDetailView";
import { BackgroundSpanDetailView, parseBackgroundSpanData } from "./BackgroundSpanDetailView";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

// Helper functions
const formatDuration = (ms: number): string => {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const parseString = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return undefined;
  return String(value);
};

// Get span kind from span
const getSpanKind = (span: TraceSpan): string | undefined => {
  const spanKindValue = span.spanKind || (span.attributes as Record<string, unknown>)["openinference.span.kind"];
  return typeof spanKindValue === "string" ? spanKindValue : undefined;
};

// Build raw data for the Attributes tab
const buildRawData = (span: TraceSpan): RawSpanData => {
  const attributes = span.attributes as Record<string, unknown>;
  return {
    input: parseString(attributes["input.value"]),
    output: parseString(attributes["output.value"]),
    attributes,
  };
};

// Main SpanDetailView component
interface SpanDetailViewProps {
  span: TraceSpan;
  className?: string;
}

export const SpanDetailView = ({ span, className }: SpanDetailViewProps) => {
  const spanKind = getSpanKind(span);
  const isLLMSpan = spanKind === "LLM";
  const isToolSpan = spanKind === "TOOL";
  const isLogEvent = span.eventName === "log";
  const isTextToSpeechEvent = span.eventName === "text_to_speech";
  const isStopEvent = span.eventName === "stop";
  const isAudioEvent = span.eventName === "audio";
  const isStartSessionEvent = span.eventName === "start_session";

  const attributes = span.attributes as Record<string, unknown>;
  const isForceClosed = attributes["voicerun.force_closed"] === true;
  const isInterrupted = attributes["voicerun.interrupted"] === true;
  const voicerunSpanType = attributes["voicerun.span.type"] as string | undefined;
  const isInputEvent = isInputEventSpan(attributes);
  const llmNormalized = normalizeLLMCompletion(attributes);
  const rawData = buildRawData(span);

  // Determine which detail view to show
  const hasLLMTabContent = isLLMSpan && isSupportedLLMProvider(llmNormalized.provider) && hasLLMContent(llmNormalized);
  const hasToolContent = isToolSpan && (rawData.input || rawData.output);
  const loggerSpanData = voicerunSpanType === "logger" ? parseLoggerSpanData(attributes) : null;
  const backgroundSpanData = voicerunSpanType === "background" ? parseBackgroundSpanData(span) : null;
  const logEventData = isLogEvent ? parseLogEventData(attributes) : null;
  const ttsEventData = isTextToSpeechEvent ? parseTextToSpeechEventData(attributes) : null;
  const stopEventData = isStopEvent ? parseStopEventData(attributes) : null;
  const audioEventData = isAudioEvent ? parseAudioEventData(attributes) : null;
  const startSessionEventData = isStartSessionEvent ? parseStartSessionEventData(attributes) : null;
  const inputEventData = isInputEvent ? parseInputEventData(attributes) : null;

  // Render the appropriate detail view
  const renderDetailView = () => {
    if (hasLLMTabContent) {
      return (
        <CompletionSpanDetailView
          data={llmNormalized}
          rawData={rawData}
        />
      );
    }

    if (hasToolContent) {
      return (
        <ToolSpanDetailView
          data={{ input: rawData.input, output: rawData.output, description: parseString(attributes["tool.description"]) }}
          rawData={rawData}
        />
      );
    }

    if (loggerSpanData) {
      return (
        <LoggerSpanDetailView
          data={loggerSpanData}
          rawData={rawData}
        />
      );
    }

    if (backgroundSpanData) {
      return (
        <BackgroundSpanDetailView
          data={backgroundSpanData}
          rawData={rawData}
        />
      );
    }

    // Input events (chain spans with voicerun.event.name) - check first before specific output events
    if (inputEventData) {
      return (
        <InputEventSpanDetailView
          inputData={inputEventData.inputData}
          outputEvents={inputEventData.outputEvents}
          rawData={rawData}
        />
      );
    }

    if (logEventData) {
      return (
        <LogEventSpanDetailView
          data={logEventData}
          rawData={rawData}
        />
      );
    }

    if (ttsEventData) {
      return (
        <TextToSpeechSpanDetailView
          data={ttsEventData}
          rawData={rawData}
        />
      );
    }

    if (stopEventData) {
      return (
        <StopEventSpanDetailView
          data={stopEventData}
          rawData={rawData}
        />
      );
    }

    if (audioEventData) {
      return (
        <AudioEventSpanDetailView
          data={audioEventData}
          rawData={rawData}
        />
      );
    }

    if (startSessionEventData) {
      return (
        <StartSessionEventSpanDetailView
          data={startSessionEventData}
          rawData={rawData}
        />
      );
    }

    // Default: just show raw attributes with padding
    return (
      <View className="p-4 overflow-y-auto h-full">
        <RawSpanDetailView data={rawData} />
      </View>
    );
  };

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      {/* Span Info Header */}
      <View className="flex flex-row flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        {/* Span Kind */}
        {spanKind && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="activity"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Kind: <span className="font-medium text-gray-800 dark:text-gray-200">{spanKind}</span>
            </Text>
          </View>
        )}
        {/* Status */}
        {span.statusCode && span.statusCode !== "UNSET" && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name={span.statusCode.toUpperCase() === "ERROR" ? "error" : "check"}
              size={14}
              className={span.statusCode.toUpperCase() === "ERROR" ? "text-red-500" : "text-green-500"}
            />
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Status:{" "}
              <span
                className={`font-medium ${span.statusCode.toUpperCase() === "ERROR" ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
              >
                {span.statusCode}
              </span>
            </Text>
          </View>
        )}
        {/* LLM Provider */}
        {llmNormalized.provider && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="bot"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Provider: <span className="font-medium text-gray-800 dark:text-gray-200">{llmNormalized.provider}</span>
            </Text>
          </View>
        )}
        {/* LLM Model */}
        {llmNormalized.model && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="sparkles"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Model: <span className="font-medium text-gray-800 dark:text-gray-200">{llmNormalized.model}</span>
            </Text>
          </View>
        )}
        {/* Temperature */}
        {llmNormalized.temperature !== undefined && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="variants"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Temp: <span className="font-medium text-gray-800 dark:text-gray-200">{llmNormalized.temperature}</span>
            </Text>
          </View>
        )}
        {/* Duration */}
        {!isForceClosed && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="clock"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Duration: <span className="font-medium text-gray-800 dark:text-gray-200">{formatDuration(span.durationMs)}</span>
            </Text>
          </View>
        )}
      </View>

      {/* Status Message (if error) */}
      {isInterrupted ? (
        <View className="px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border-b border-gray-200 dark:border-gray-700">
          <Text className="text-sm text-orange-600 dark:text-orange-400">This span was interrupted before it could complete</Text>
        </View>
      ) : span.statusMessage ? (
        <View className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-gray-200 dark:border-gray-700">
          <Text className="text-sm text-red-600 dark:text-red-400">{safeString(span.statusMessage)}</Text>
        </View>
      ) : null}

      {/* Token Counts (LLM only) */}
      {llmNormalized.tokenCounts && (
        <View className="flex flex-row items-center gap-4 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 border-b border-gray-200 dark:border-gray-700">
          <Text className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Tokens:</Text>
          {llmNormalized.tokenCounts.prompt !== undefined && (
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Prompt: <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{llmNormalized.tokenCounts.prompt.toLocaleString()}</span>
            </Text>
          )}
          {llmNormalized.tokenCounts.completion !== undefined && (
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Completion:{" "}
              <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{llmNormalized.tokenCounts.completion.toLocaleString()}</span>
            </Text>
          )}
          {llmNormalized.tokenCounts.total !== undefined && (
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Total: <span className="font-mono font-medium text-indigo-700 dark:text-indigo-300">{llmNormalized.tokenCounts.total.toLocaleString()}</span>
            </Text>
          )}
        </View>
      )}

      {/* Detail View Content */}
      <View className="flex-1 overflow-hidden">{renderDetailView()}</View>
    </View>
  );
};
